import type {
  Outcome,
  PrimaryTask,
  SessionSortDir,
  SessionSortField,
  SessionSummary,
} from '../api/types';
import { displayUserId } from '../lib/userId';

interface Props {
  sessions: SessionSummary[];
  total: number;
  limit: number;
  offset: number;
  loading: boolean;
  sortBy: SessionSortField;
  sortDir: SessionSortDir;
  onSortChange: (sortBy: SessionSortField, sortDir: SessionSortDir) => void;
  onSelect: (session: SessionSummary) => void;
  onPageChange: (nextOffset: number) => void;
}

interface SortableHeaderProps {
  label: string;
  field: SessionSortField;
  defaultDir: SessionSortDir;
  sortBy: SessionSortField;
  sortDir: SessionSortDir;
  onSortChange: (sortBy: SessionSortField, sortDir: SessionSortDir) => void;
  className?: string;
}

function SortableHeader({
  label,
  field,
  defaultDir,
  sortBy,
  sortDir,
  onSortChange,
  className,
}: SortableHeaderProps): JSX.Element {
  const isActive = sortBy === field;
  const handleClick = (): void => {
    if (!isActive) {
      onSortChange(field, defaultDir);
      return;
    }
    onSortChange(field, sortDir === 'asc' ? 'desc' : 'asc');
  };
  const arrow = isActive ? (sortDir === 'asc' ? '▲' : '▼') : '';
  return (
    <th
      className={`sessions-table-th-sortable${isActive ? ' is-sorted' : ''}${className ? ` ${className}` : ''}`}
      onClick={handleClick}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          handleClick();
        }
      }}
      tabIndex={0}
      role="button"
      aria-sort={isActive ? (sortDir === 'asc' ? 'ascending' : 'descending') : 'none'}
    >
      {label}
      {arrow && <span className="sessions-table-sort-arrow"> {arrow}</span>}
    </th>
  );
}

const TASK_LABELS: Record<PrimaryTask, string> = {
  page_migration: 'Migration',
  refinement: 'Refinement',
  skill_authoring: 'Skill auth',
  content_generation: 'Content gen',
  other: 'Other',
};

const OUTCOME_COLORS: Record<Outcome, string> = {
  completed: '#10b981',
  partial: '#f59e0b',
  exploratory: '#6366f1',
  stuck: '#ef4444',
  abandoned: '#71717a',
};

function formatDuration(min: number): string {
  if (min < 1) return '<1m';
  if (min < 60) return `${min}m`;
  const hours = Math.floor(min / 60);
  const remaining = min % 60;
  return remaining > 0 ? `${hours}h ${remaining}m` : `${hours}h`;
}

function formatStartedAt(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
}

export function SessionsTable({
  sessions,
  total,
  limit,
  offset,
  loading,
  sortBy,
  sortDir,
  onSortChange,
  onSelect,
  onPageChange,
}: Props): JSX.Element {
  const start = total === 0 ? 0 : offset + 1;
  const end = Math.min(offset + limit, total);

  const sortHeaderProps = { sortBy, sortDir, onSortChange };

  return (
    <div className="sessions-table-wrapper">
      <table className="sessions-table">
        <thead>
          <tr>
            <SortableHeader label="Started" field="started_at" defaultDir="desc" {...sortHeaderProps} />
            <SortableHeader label="User" field="user_id" defaultDir="asc" {...sortHeaderProps} />
            <th>Harness</th>
            <SortableHeader label="Task" field="primary_task" defaultDir="asc" {...sortHeaderProps} />
            <SortableHeader label="Outcome" field="outcome" defaultDir="asc" {...sortHeaderProps} />
            <SortableHeader label="Feel" field="session_feel" defaultDir="asc" {...sortHeaderProps} />
            <SortableHeader label="Duration" field="duration_min" defaultDir="desc" {...sortHeaderProps} />
            <SortableHeader label="Events" field="event_count" defaultDir="desc" {...sortHeaderProps} />
            <th>What happened</th>
          </tr>
        </thead>
        <tbody>
          {loading && sessions.length === 0 && (
            <tr>
              <td colSpan={9} className="sessions-table-loading">Loading sessions…</td>
            </tr>
          )}
          {!loading && sessions.length === 0 && (
            <tr>
              <td colSpan={9} className="sessions-table-empty">No sessions in this window.</td>
            </tr>
          )}
          {sessions.map((s) => (
            <tr
              key={s.sessionId}
              className="sessions-table-row"
              onClick={() => onSelect(s)}
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  onSelect(s);
                }
              }}
            >
              <td className="sessions-table-time">{formatStartedAt(s.startedAt)}</td>
              <td className="sessions-table-user">{displayUserId(s.userId)}</td>
              <td className="sessions-table-harness">{s.harness ?? '—'}</td>
              <td>
                {s.primaryTask ? (
                  <span className="badge badge-task">{TASK_LABELS[s.primaryTask]}</span>
                ) : (
                  <span className="badge badge-unclassified">unclassified</span>
                )}
              </td>
              <td>
                {s.outcome ? (
                  <span
                    className="badge badge-outcome"
                    style={{ backgroundColor: `${OUTCOME_COLORS[s.outcome]}22`, color: OUTCOME_COLORS[s.outcome] }}
                  >
                    {s.outcome}
                  </span>
                ) : (
                  <span className="badge badge-unclassified">—</span>
                )}
              </td>
              <td className="sessions-table-feel">{s.sessionFeel ?? '—'}</td>
              <td className="sessions-table-duration">{formatDuration(s.durationMin)}</td>
              <td className="sessions-table-events">{s.eventCount.toLocaleString('en-US')}</td>
              <td className="sessions-table-headline">
                {s.journeyHeadline ?? <span className="muted">No journey yet</span>}
                {s.safetyFlags.length > 0 && (
                  <span className="badge badge-flag" title={s.safetyFlags.join(', ')}>
                    ⚑ {s.safetyFlags.length}
                  </span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {total > 0 && (
        <div className="sessions-pagination">
          <span>
            Showing {start}–{end} of {total.toLocaleString('en-US')}
          </span>
          <div className="sessions-pagination-buttons">
            <button
              type="button"
              disabled={offset === 0 || loading}
              onClick={() => onPageChange(Math.max(0, offset - limit))}
            >
              ← Prev
            </button>
            <button
              type="button"
              disabled={end >= total || loading}
              onClick={() => onPageChange(offset + limit)}
            >
              Next →
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
